import { useLingui } from '@lingui/react';
import { ClipboardList, AlertCircle, Clock, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

interface StatCardData {
  key: string;
  icon: React.ReactNode;
  value: number;
  label: string;
  color: string;
  alertWhenPositive?: boolean;
}

interface WorkflowStatsStripProps {
  pendingCount: number;
  overdueCount: number;
  dueTodayCount: number;
  myTasksCount: number;
  loading: boolean;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}

export function WorkflowStatsStrip({
  pendingCount,
  overdueCount,
  dueTodayCount,
  myTasksCount,
  loading,
  activeFilter,
  onFilterClick,
}: WorkflowStatsStripProps) {
  const { i18n } = useLingui();

  const cards: StatCardData[] = [
    {
      key: 'pending',
      icon: <ClipboardList className="h-5 w-5" aria-hidden="true" />,
      value: pendingCount,
      label: i18n._({ id: 'workflow.stats.pending', message: 'Pending' }),
      color: 'text-info-on-surface',
    },
    {
      key: 'overdue',
      icon: <AlertCircle className="h-5 w-5" aria-hidden="true" />,
      value: overdueCount,
      label: i18n._({ id: 'workflow.stats.overdue', message: 'Overdue' }),
      color: 'text-destructive',
      alertWhenPositive: true,
    },
    {
      key: 'dueToday',
      icon: <Clock className="h-5 w-5" aria-hidden="true" />,
      value: dueTodayCount,
      label: i18n._({ id: 'workflow.stats.dueToday', message: 'Due Today' }),
      color: 'text-warning-on-surface',
    },
    {
      key: 'myTasks',
      icon: <User className="h-5 w-5" aria-hidden="true" />,
      value: myTasksCount,
      label: i18n._({ id: 'workflow.stats.myTasks', message: 'My Tasks' }),
      color: 'text-primary',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-strip">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-4 w-16 mb-2" />
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-strip">
      {cards.map((card) => (
        // A filter toggle: a real button, so it is reachable and announced as pressed.
        <button
          type="button"
          key={card.key}
          className={cn(
            'rounded-lg border bg-card p-4 text-left text-card-foreground shadow-sm transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            activeFilter === card.key && 'ring-2 ring-primary',
          )}
          onClick={() => onFilterClick(activeFilter === card.key ? '' : card.key)}
          aria-pressed={activeFilter === card.key}
          data-testid={`stat-${card.key}`}
        >
          <span className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{card.label}</span>
            <span className={card.color}>{card.icon}</span>
          </span>
          <span
            className={cn(
              'mt-1 block text-2xl font-semibold tabular-nums',
              card.alertWhenPositive && card.value > 0 ? 'text-destructive' : 'text-foreground',
            )}
          >
            {formatNumber(card.value, i18n.locale)}
          </span>
        </button>
      ))}
    </div>
  );
}
